import { SemanticModelAdapter } from "./semantic-model-adapter.js";
import { isCreateClassOperation, isCreateGeneralizationOperation, isCreateRelationshipOperation, isDeleteEntityOperation, isModifyClassOperation, isModifyGeneralizationOperation, isModifyRelationOperation, } from "./operations/index.js";
import { isCreateClassUsageOperation, isCreateRelationshipUsageOperation, isModifyClassUsageOperation, isModifyRelationshipUsageOperation, } from "./usage/operations/index.js";
import { createDefaultSemanticModelProfileOperationExecutor } from "./profile/operations/index.js";
function uuid() {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
}
/**
 * Semantic model, that is writable.
 */
export class WritableSemanticModelAdapter extends SemanticModelAdapter {
    constructor(entityModel) {
        super(entityModel);
    }
    executeOperation(operation) {
        const results = this.executeOperations([operation]);
        return results[0];
    }
    executeOperations(operations) {
        const updatedCollector = {};
        const removedCollector = [];
        const getEntity = identifier => this.entityModel.entities[identifier];
        const change = (updated, removed) => {
            for (const [id, entity] of Object.entries(updated)) {
                updatedCollector[id] = entity;
            }
            removed.forEach(item => removedCollector.push(item));
        };
        const profileExecutor = createDefaultSemanticModelProfileOperationExecutor({ createIdentifier: uuid }, { entity: identifier => getEntity(identifier) ?? null }, { change });
        const result = [];
        for (const operation of operations) {
            if (isCreateClassOperation(operation)) {
                result.push(handleCreateClassOperation(getEntity, change, operation));
            }
            else if (isModifyClassOperation(operation)) {
                result.push(handleModifyClassOperation(getEntity, change, operation));
            }
            else if (isCreateRelationshipOperation(operation)) {
                result.push(handleCreateRelationshipOperation(getEntity, change, operation));
            }
            else if (isModifyRelationOperation(operation)) {
                result.push(handleModifyRelationOperation(getEntity, change, operation));
            }
            else if (isCreateGeneralizationOperation(operation)) {
                result.push(handleCreateGeneralizationOperation(getEntity, change, operation));
            }
            else if (isModifyGeneralizationOperation(operation)) {
                result.push(handleModifyGeneralizationOperation(getEntity, change, operation));
            }
            else if (isDeleteEntityOperation(operation)) {
                result.push(handleDeleteEntityOperation(getEntity, change, operation));
            }
            else if (isCreateClassUsageOperation(operation)) {
                result.push(handleCreateClassUsageOperation(getEntity, change, operation));
            }
            else if (isModifyClassUsageOperation(operation)) {
                result.push(handleModifyClassUsageOperation(getEntity, change, operation));
            }
            else if (isCreateRelationshipUsageOperation(operation)) {
                result.push(handleCreateRelationshipUsageOperation(getEntity, change, operation));
            }
            else if (isModifyRelationshipUsageOperation(operation)) {
                result.push(handleModifyRelationshipUsageOperation(getEntity, change, operation));
            }
            else {
                const operationResult = profileExecutor.executeOperation(operation);
                if (operationResult !== null) {
                    result.push({
                        success: true,
                        id: operationResult.created[0],
                    });
                }
                else {
                    // Unknown operation.
                    result.push({
                        success: false,
                    });
                }
            }
        }
        // We execute all updates at once.
        this.entityModel.change(updatedCollector, removedCollector);
        //
        return result;
    }
}
function handleCreateClassOperation(getEntity, change, operation) {
    let id = operation.entity.id;
    // Generate random id if not provided
    if (id === undefined) {
        id = uuid();
    }
    if (getEntity(id)) {
        return {
            success: false,
        };
    }
    const newClass = {
        // Store any other data
        ...operation.entity,
        id,
        iri: operation.entity.iri ?? null,
        type: ["class"],
        name: operation.entity.name ?? {},
        description: operation.entity.description ?? {},
        externalDocumentationUrl: operation.entity.externalDocumentationUrl ?? null,
    };
    change({ [id]: newClass }, []);
    return {
        success: true,
        id,
    };
}
function handleModifyClassOperation(getEntity, change, operation) {
    if (!getEntity(operation.id)) {
        return {
            success: false,
        };
    }
    change({ [operation.id]: { ...getEntity(operation.id), ...operation.entity } }, []);
    return {
        success: true,
    };
}
function handleCreateRelationshipOperation(getEntity, change, operation) {
    let id = operation.entity.id;
    // Generate random id if not provided
    if (id === undefined) {
        id = uuid();
    }
    if (getEntity(id)) {
        return {
            success: false,
        };
    }
    const relationship = {
        ...operation.entity,
        id,
        type: ["relationship"],
        iri: operation.entity.iri ?? null,
        name: operation.entity.name ?? {},
        description: operation.entity.description ?? {},
        ends: [
            {
                ...operation.entity.ends?.[0],
                name: operation.entity.ends?.[0]?.name ?? {},
                description: operation.entity.ends?.[0]?.description ?? {},
                cardinality: operation.entity.ends?.[0]?.cardinality,
                concept: operation.entity.ends?.[0]?.concept ?? null,
                iri: operation.entity.ends?.[0]?.iri ?? null,
                externalDocumentationUrl: operation.entity.ends?.[0]?.externalDocumentationUrl ?? null,
            },
            {
                ...operation.entity.ends?.[1],
                name: operation.entity.ends?.[1]?.name ?? {},
                description: operation.entity.ends?.[1]?.description ?? {},
                cardinality: operation.entity.ends?.[1]?.cardinality,
                concept: operation.entity.ends?.[1]?.concept ?? null,
                iri: operation.entity.ends?.[1]?.iri ?? null,
                externalDocumentationUrl: operation.entity.ends?.[1]?.externalDocumentationUrl ?? null,
            },
        ],
    };
    change({ [id]: relationship }, []);
    return {
        success: true,
        id,
    };
}
function handleModifyRelationOperation(getEntity, change, operation) {
    const oldRelationship = getEntity(operation.id);
    if (!oldRelationship) {
        return {
            success: false,
        };
    }
    const updatedRelationship = {
        ...oldRelationship,
        ...operation.entity,
        ends: [
            {
                ...oldRelationship.ends[0],
                ...operation.entity.ends?.[0],
            },
            {
                ...oldRelationship.ends[1],
                ...operation.entity.ends?.[1],
            },
        ],
        name: operation.entity.name ?? oldRelationship.name,
        description: operation.entity.description ?? oldRelationship.description,
        iri: operation.entity.iri ?? oldRelationship.iri,
    };
    change({ [operation.id]: updatedRelationship }, []);
    return {
        success: true,
    };
}
function handleCreateGeneralizationOperation(getEntity, change, operation) {
    let id = operation.entity.id;
    // Generate random id if not provided
    if (id === undefined) {
        id = uuid();
    }
    if (getEntity(id)) {
        return {
            success: false,
        };
    }
    const generalization = {
        id,
        iri: operation.entity.iri ?? null,
        child: operation.entity.child ?? "",
        parent: operation.entity.parent ?? "",
        type: ["generalization"],
    };
    change({ [id]: generalization }, []);
    return {
        success: true,
        id,
    };
}
function handleModifyGeneralizationOperation(getEntity, change, operation) {
    if (!getEntity(operation.id)) {
        return {
            success: false,
        };
    }
    change({ [operation.id]: { ...getEntity(operation.id), ...operation.entity } }, []);
    return {
        success: true,
    };
}
function handleDeleteEntityOperation(getEntity, change, operation) {
    if (!getEntity(operation.id)) {
        return {
            success: false,
        };
    }
    change({}, [operation.id]);
    return {
        success: true,
    };
}
function handleCreateClassUsageOperation(getEntity, change, operation) {
    let id = operation.entity.id;
    // Generate random id if not provided
    if (id === undefined) {
        id = uuid();
    }
    if (getEntity(id)) {
        return {
            success: false,
        };
    }
    const cls = {
        ...operation.entity,
        id,
        usageOf: operation.entity.usageOf,
        type: ["class-usage"],
        iri: operation.entity.iri ?? null,
        name: operation.entity.name ?? null,
        description: operation.entity.description ?? null,
        usageNote: operation.entity.usageNote ?? null,
    };
    change({ [id]: cls }, []);
    return {
        success: true,
        id,
    };
}
function handleModifyClassUsageOperation(getEntity, change, operation) {
    if (!getEntity(operation.id)) {
        return {
            success: false,
        };
    }
    change({ [operation.id]: { ...getEntity(operation.id), ...operation.entity } }, []);
    return {
        success: true,
    };
}
function handleCreateRelationshipUsageOperation(getEntity, change, operation) {
    let id = operation.entity.id;
    // Generate random id if not provided
    if (id === undefined) {
        id = uuid();
    }
    if (getEntity(id)) {
        return {
            success: false,
        };
    }
    const relationship = {
        ...operation.entity,
        usageNote: operation.entity.usageNote ?? null,
        id,
        type: ["relationship-usage"],
        iri: operation.entity.iri ?? null,
        usageOf: operation.entity.usageOf,
        name: operation.entity.name ?? null,
        description: operation.entity.description ?? null,
        ends: [
            {
                ...operation.entity.ends?.[0],
                name: operation.entity.ends?.[0]?.name ?? null,
                description: operation.entity.ends?.[0]?.description ?? null,
                cardinality: operation.entity.ends?.[0]?.cardinality ?? null,
                concept: operation.entity.ends?.[0]?.concept ?? null,
                usageNote: operation.entity.ends?.[0]?.usageNote ?? null,
                iri: operation.entity.ends?.[0]?.iri ?? null,
            },
            {
                ...operation.entity.ends?.[1],
                name: operation.entity.ends?.[1]?.name ?? null,
                description: operation.entity.ends?.[1]?.description ?? null,
                cardinality: operation.entity.ends?.[1]?.cardinality ?? null,
                concept: operation.entity.ends?.[1]?.concept ?? null,
                usageNote: operation.entity.ends?.[1]?.usageNote ?? null,
                iri: operation.entity.ends?.[1]?.iri ?? null,
            },
        ],
    };
    change({ [id]: relationship }, []);
    return {
        success: true,
        id,
    };
}
function handleModifyRelationshipUsageOperation(getEntity, change, operation) {
    const oldRelationship = getEntity(operation.id);
    if (!oldRelationship) {
        return {
            success: false,
        };
    }
    const updatedRelationship = {
        ...oldRelationship,
        ...operation.entity,
        usageNote: operation.entity.usageNote ?? oldRelationship.usageNote,
        name: operation.entity.name ?? oldRelationship.name,
        description: operation.entity.description ?? oldRelationship.description,
        ends: [
            {
                ...oldRelationship.ends[0],
                ...operation.entity.ends?.[0],
            },
            {
                ...oldRelationship.ends[1],
                ...operation.entity.ends?.[1],
            },
        ],
    };
    change({ [operation.id]: updatedRelationship }, []);
    return {
        success: true,
    };
}
//# sourceMappingURL=writable-semantic-model-adapter.js.map